import {
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  useTheme,
  Card,
  CardContent,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import BirthDayIcon from "../icons/BirthDayIcon";
import JubileeIcon from "../icons/JubileeIcon";
import HealthBetterIcon from "../icons/HealthBetterIcon";
import HealthIcon from "../icons/HealthIcon";
import useAfasData from "../hooks/useAfasData";
import { LanguageContext } from "../context/LanguageContext";

// Define the types of news items
type NewsType = "birthday" | "jubilee" | "health" | "health_better";

interface NewsItem {
  id: string;
  type: NewsType;
  name: string;
  description: string;
}

const getIconByType = (type: NewsType) => {
  switch (type) {
    case "birthday":
      return <BirthDayIcon sx={{ fontSize: "1rem", width: "19px", height: "19px" }} />;
    case "jubilee":
      return <JubileeIcon sx={{ fontSize: "1rem", width: "19px", height: "19px" }} />;
    case "health":
      return <HealthIcon sx={{ fontSize: "1rem", width: "19px", height: "19px" }} />;
    case "health_better":
      return <HealthBetterIcon sx={{ fontSize: "1rem", width: "19px", height: "19px" }} />;
    default:
      return <BirthDayIcon sx={{ fontSize: "1rem", width: "19px", height: "19px" }} />;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAvatarColorByType = (type: NewsType, theme: any) => {
  switch (type) {
    case "birthday":
      return theme.palette.purple.main;
    case "jubilee":
      return theme.palette.primary.main;
    case "health":
      return theme.palette.green.main;
    case "health_better":
      return theme.palette.green.main;
    default:
      return theme.palette.grey[500];
  }
};

const NewsComponent: React.FC = () => {
  const { strings } = useContext(LanguageContext);
  const itemsPerPage = 8;
  const [items, setItems] = useState<NewsItem[]>([]);
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);

  const { data: teamList, isLoading: teamIsLoading } = useAfasData("/team/news");
  useEffect(() => {
    if (!teamIsLoading && teamList) {
      setItems(teamList);
    }
  }, [teamList]);

  const totalPages = Math.ceil(items.length / itemsPerPage) > 0 ? Math.ceil(items.length / itemsPerPage) : 1;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  return (
    <Box>
      <Card sx={{ minHeight: "240px" }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h2" component="h2">
              {strings.news.title}
            </Typography>
            <Box>
              {totalPages !== 1 && (
                <>
                  <IconButton onClick={goToPrevPage} disabled={currentPage === 1} size="small">
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography component="span" sx={{ mx: 1 }}>
                    {currentPage}/
                    <Typography component={"span"} variant="body1" fontWeight={"bold"}>
                      {totalPages}
                    </Typography>
                  </Typography>
                  <IconButton onClick={goToNextPage} disabled={currentPage === totalPages} size="small">
                    <ArrowForwardIcon />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
          <List sx={{ width: "100%", columnCount: 2 }}>
            {teamIsLoading && <CircularProgress />}
            {!teamIsLoading && currentItems.length === 0 && (
              <ListItem disableGutters alignItems="center">
                <ListItemText primary={strings.news.none} />
              </ListItem>
            )}
            {currentItems.map((item) => (
              <ListItem disableGutters key={item.id} alignItems="center">
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: getAvatarColorByType(item.type, theme),
                      color: "#fff",
                    }}
                  >
                    {getIconByType(item.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  sx={{ ml: 1 }}
                  primary={
                    <>
                      {item.name} - {item.description}
                      {item.id.includes("abp") && <Chip label="ABP" size="small" sx={{ ml: 1 }} />}
                      {item.id.includes("service") && <Chip label="UT" size="small" sx={{ ml: 1 }} />}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NewsComponent;
